import { IncomingMessage, ServerResponse } from 'http'
import { useMemo } from 'react'
import * as Sentry from '@sentry/node'
import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import fetch from 'cross-fetch'

const isProd = process.env.NODE_ENV === 'production'
const isServer = typeof window === 'undefined'

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined

export type ResolverContext = {
  req?: IncomingMessage
  res?: ServerResponse
}

function createIsomorphLink(context?: ResolverContext) {
  // ssr시 SchemaLink를 쓰는경우, resolver가 바로 호출되어
  // api server의 middleware, context처리 skip함
  //
  const isServer = typeof window === 'undefined'

  const port = '3000'
  const host = isServer ? `http://localhost:${port}` : ''
  const headers = context?.req?.headers

  if (headers && !headers['referer']) {
    // Adds this for debugging purpose in ssr mode
    headers['referer'] = `${host}${context!.req!.url}`
  }

  const { BatchHttpLink } = require('@apollo/client/link/batch-http')
  return new BatchHttpLink({
    uri: `${host}/api/graphql`,
    credentials: isServer ? 'include' : 'same-origin',
    fetch,
    ...(isServer && headers ? { headers } : {}),
    batchMax: 10,
    batchInterval: 10,
  })
}

function createApolloClient(context?: ResolverContext) {
  return new ApolloClient({
    devtools: {
      enabled: !isServer && !isProd,
    },
    ssrMode: isServer,
    link: ApolloLink.from([createIsomorphLink(context)]),
    cache: new InMemoryCache(),
    // cache가 id필드를 참조하는데,
    // CouponDomesticInstance처럼 id가 null인 데이터가 있을 경우 문제가 됨
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
    },
  })
}

export function initializeApollo(
  initialState: any = null,
  // Pages with Next.js data fetching methods, like `getStaticProps`, can send
  // a custom context which will be used by `SchemaLink` to server render pages
  context?: ResolverContext
) {
  const _apolloClient = apolloClient ?? createApolloClient(context)

  // Get existing cache, loaded during client side data fetching
  const existingCache = _apolloClient.extract()

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  try {
    if (initialState) {
      _apolloClient.cache.restore({ ...existingCache, ...initialState })
    }
  } catch (er) {
    console.dir(initialState, { depth: null })
    Sentry.captureException(er)
    throw er
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient

  return _apolloClient
}

export function useApollo(initialState: any) {
  const store = useMemo(() => initializeApollo(initialState), [initialState])
  return store
}
